import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle, Compass, Loader2, X } from 'lucide-react';

import { snapshotsAPI } from '../api/client';
import type { AreaHealthDefinition } from '../types';

interface SnapshotHealthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export default function SnapshotHealthModal({ isOpen, onClose, onSaved }: SnapshotHealthModalProps) {
  const [definitions, setDefinitions] = useState<AreaHealthDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [carriedForward, setCarriedForward] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    snapshotsAPI
      .getCurrentPeriod()
      .then((period) => {
        setDefinitions(period.definitions);
        setCarriedForward(period.carried_forward);
      })
      .catch(() => setDefinitions([]))
      .finally(() => setLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  const updateDefinition = (categoryId: number, healthyLooksLike: string) => {
    setDefinitions((previous) =>
      previous.map((definition) =>
        definition.category === categoryId
          ? { ...definition, healthy_looks_like: healthyLooksLike }
          : definition,
      ),
    );
  };

  const saveDefinitions = async () => {
    const filled = definitions.filter(
      (definition) => !definition.skipped && definition.healthy_looks_like.trim(),
    );
    setSaving(true);
    try {
      await snapshotsAPI.updateHealthDefinitions(
        filled.map((definition) => ({
          category: definition.category,
          healthy_looks_like: definition.healthy_looks_like,
          positive_indicators: definition.positive_indicators || [],
          slipping_signals: definition.slipping_signals || [],
        })),
      );
      toast.success('Snapshot definitions saved.');
      onSaved?.();
      onClose();
    } catch {
      toast.error('Could not save Snapshot definitions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Snapshot Health Definitions"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-navy flex items-center gap-2">
              <Compass size={20} />
              Snapshot Health Definitions
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Review or update what healthy looks like in each area for your current season.
              Save the ones you finish now — you can come back for the rest. You’ll need every
              area before you finalize a Snapshot.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1 text-gray-400 hover:text-navy"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
              <Loader2 size={16} className="animate-spin" />
              Loading Snapshot definitions…
            </div>
          ) : definitions.length === 0 ? (
            <div className="rounded-xl bg-off-white p-4">
              <p className="text-sm text-gray-600">
                Your Snapshot areas aren’t available yet. Start your first Snapshot to set them up.
              </p>
              <Link
                to="/snapshots/new"
                className="inline-flex items-center gap-2 mt-3 text-sm font-semibold text-navy hover:underline"
              >
                Start a Snapshot
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {carriedForward && (
                <p className="text-sm text-navy bg-off-white rounded-lg p-3">
                  These values were carried forward from last season. Saving will make them
                  your definitions for the current season.
                </p>
              )}
              {definitions.map((definition) => (
                <div
                  key={definition.category}
                  className={`rounded-xl border p-4 ${
                    definition.skipped ? 'border-gray-100 bg-gray-50' : 'border-gray-200'
                  }`}
                >
                  <div className="mb-2">
                    <label
                      htmlFor={`snapshot-definition-${definition.category}`}
                      className="font-semibold text-navy"
                    >
                      {definition.category_name}
                    </label>
                    {definition.scripture_reference && (
                      <p className="text-xs text-gray-500 italic">
                        {definition.scripture_reference}
                      </p>
                    )}
                  </div>
                  {definition.skipped ? (
                    <p className="text-sm text-gray-500">
                      This area is currently marked Skip / N/A in your Snapshot.
                    </p>
                  ) : (
                    <textarea
                      id={`snapshot-definition-${definition.category}`}
                      value={definition.healthy_looks_like}
                      onChange={(event) =>
                        updateDefinition(definition.category, event.target.value)
                      }
                      rows={3}
                      placeholder={`What does a healthy “${definition.category_name}” look like for you this season?`}
                      className={`${inputClass} resize-none`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {!loading && definitions.length > 0 && (
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveDefinitions}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-navy text-white rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50"
            >
              {saving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <CheckCircle size={18} />
              )}
              Save Definitions
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
